<script setup lang="ts">
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { computed, ref, defineEmits } from 'vue'
  import {
    type CustomerCase as Case,
    AiAugmentedByMapperKeysEnum as DatamapperKey
  } from '@/types/generated'
  import InputField from '@/components/admin/form/InputField.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { Loader2 } from 'lucide-vue-next'
  import { storeToRefs } from 'pinia'
  import { useToast } from '@/components/ui/toast'
  import { useMetrics, MetricEventType } from '@/composables/useMetrics'

  const emit = defineEmits(['close'])

  // Translations
  const { t } = useTranslation({
    en: {
      repairCosts: 'Repair Costs',
      values: 'Values',
      options: 'Options',
      total: 'Total',
      netRepairCosts: 'Net Repair Costs',
      grossRepairCosts: 'Gross Repair Costs',
      valueLoss: 'Value Loss',
      netReplacementValue: 'Net Replacement Value',
      grossReplacementValue: 'Gross Replacement Value',
      grossResidualValue: 'Gross Residual Value',
      netGrossReplacementCosts: 'Net-Gross Replacement Costs',
      grossGrossReplacementCosts: 'Gross-Gross Replacement Costs',
      isReplacementPurchase: 'Replacement Purchase',
      isInvoiceWithVat: 'Invoice with VAT',
      isRepair: 'Repair',
      isSixMonthExtension: 'Six-month Extension',
      isDifferentialTax: 'Differential Tax',
      isRegularTax: 'Regular Tax',
      save: 'Save',
      updatedFields: 'Updated Fields'
    },
    de: {
      repairCosts: 'Reparaturkosten',
      values: 'Werte',
      options: 'Optionen',
      total: 'Gesamt',
      netRepairCosts: 'Nettoreparaturkosten',
      grossRepairCosts: 'Bruttoreparaturkosten',
      valueLoss: 'Wertminderung',
      netReplacementValue: 'Nettowiederbeschaffungswert',
      grossReplacementValue: 'Bruttowiederbeschaffungswert',
      grossResidualValue: 'Bruttorestwert',
      netGrossReplacementCosts: 'Netto-Brutto-Wiederbeschaffungsaufwand',
      grossGrossReplacementCosts: 'Brutto-Brutto-Wiederbeschaffungsaufwand',
      isReplacementPurchase: 'Ersatzbeschaffung',
      isInvoiceWithVat: 'Rechnung mit Mehrwertsteuerausweis',
      isRepair: 'Reparatur',
      isSixMonthExtension: 'Sechsmonatige Weiternutzung',
      isDifferentialTax: 'Differenzsteuer',
      isRegularTax: 'Regelsteuer',
      save: 'Speichern',
      updatedFields: 'Felder aktualisiert'
    }
  })

  const caseStore = useCaseStore()
  const { isSavingCase } = storeToRefs(useCaseStore())
  const caseRecord = computed<Case>(() => caseStore.caseRecord as Case)
  const { trackEvent } = useMetrics()

  const isReplacementPurchase = ref<boolean>(false)
  const isInvoiceWithVat = ref<boolean>(false)
  const isRepair = ref<boolean>(false)
  const isSixMonthExtension = ref<boolean>(false)
  const isDifferentialTax = ref<boolean>(false)
  const isRegularTax = ref<boolean>(false)

  const grossRepairCosts = ref<number>(0)
  const netRepairCosts = computed(() => {
    return grossRepairCosts.value / 1.19
  })

  const merantileValueLoss = ref<number>(0)

  const grossReplacementValue = ref<number>(0)
  const netReplacementValue = computed(() => {
    if (isDifferentialTax.value) {
      return grossReplacementValue.value / 1.025
    } else if (isRegularTax.value) {
      return grossReplacementValue.value / 1.19
    }
    return grossReplacementValue.value
  })

  const grossResidualValue = ref<number>(0)

  const netGrossReplacementCosts = computed(() => {
    return netReplacementValue.value - grossResidualValue.value
  })
  const grossGrossReplacementCosts = computed(() => {
    return grossReplacementValue.value - grossResidualValue.value
  })

  const totalDamageCosts = ref<number>(0)

  // Load initial data
  const loadInitialData = async () => {
    isDifferentialTax.value =
      (await caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_DIFFERENCIAL_TAX)) ===
      true
    isRegularTax.value =
      (await caseStore.getDataWithMapperKey(DatamapperKey.LAW_TRAFFIC_LAW_REGULAR_TAX)) === true

    const grossRepairCostsValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITH_VAT
    )
    grossRepairCosts.value = grossRepairCostsValue ? parseFloat(grossRepairCostsValue) : 0

    const merantileValueLossValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_MERANTILE_VALUE_LOSS
    )
    merantileValueLoss.value = merantileValueLossValue ? parseFloat(merantileValueLossValue) : 0

    const grossReplacementValueValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_REPLACEMENT_VALUE
    )
    grossReplacementValue.value = grossReplacementValueValue
      ? parseFloat(grossReplacementValueValue)
      : 0

    const grossResidualValueValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_RESIDUAL_VALUE
    )
    grossResidualValue.value = grossResidualValueValue ? parseFloat(grossResidualValueValue) : 0

    const totalDamageCostsValue = await caseStore.getDataWithMapperKey(
      DatamapperKey.LAW_TRAFFIC_LAW_CALCULATED_TOTAL_DAMAGE_COSTS
    )
    totalDamageCosts.value = totalDamageCostsValue ? parseFloat(totalDamageCostsValue) : 0
  }

  // Call loadInitialData when component is mounted
  loadInitialData()

  const { toast } = useToast()

  // Save handler for all fields
  const handleSaveAll = async () => {
    await caseStore.bulkUpdateDataWithMapperKeys({
      [DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITH_VAT]: grossRepairCosts.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_MERANTILE_VALUE_LOSS]: merantileValueLoss.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_REPLACEMENT_VALUE]: grossReplacementValue.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_RESIDUAL_VALUE]: grossResidualValue.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_DIFFERENCIAL_TAX]: isDifferentialTax.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_REGULAR_TAX]: isRegularTax.value,
      [DatamapperKey.LAW_TRAFFIC_LAW_CALCULATED_TOTAL_DAMAGE_COSTS]: formatToTwoDecimals(
        totalDamageCostsCalculated.value
      )
    })

    // Track calculator saved event
    await trackEvent(MetricEventType.CALCULATOR_SAVED, {
      calculator_type: 'Vehicle Damage Settlement',
      calculated_sum: totalDamageCostsCalculated.value,
      gross_repair_costs: grossRepairCosts.value,
      merantile_value_loss: merantileValueLoss.value,
      gross_replacement_value: grossReplacementValue.value,
      gross_residual_value: grossResidualValue.value,
      net_repair_costs: netRepairCosts.value,
      net_replacement_value: netReplacementValue.value
    })

    toast({
      title: t('updatedFields'),
      description: 'Vehicle damage fields updated',
      variant: 'default'
    })
    emit('close')
  }

  // Computed property for the total damage costs calculation
  const totalDamageCostsCalculated = computed(() => {
    // Map the variables to the Excel formula variables
    const B1 = netRepairCosts.value // netRepairCost
    const B2 = grossRepairCosts.value // grossRepairCost
    const B3 = merantileValueLoss.value // merantileValueLoss
    const B4 = netReplacementValue.value // netReplacementValue
    const B5 = grossReplacementValue.value // grossReplacementValue
    const B6 = grossResidualValue.value // grossResidualValue
    const B7 = netGrossReplacementCosts.value // netGrossReplacementCosts
    const B8 = grossGrossReplacementCosts.value // grossGrossReplacementCosts
    const D10 = isReplacementPurchase.value // isReplacementPurchase
    const D11 = isInvoiceWithVat.value // isInvoiceWithVat
    const D12 = isRepair.value // isRepair
    const D13 = isSixMonthExtension.value // isSixMonthExtension

    // Implement the formula: =IF(AND(ISBLANK(B5),D11=1,D12=1),B2+B3,IF(ISBLANK(B5),B1+B3,IF(AND(B2+B3<B5,D11=1,D12=1),B2+B3,IF(AND(B1+B3<B7,B2+B3>B8,D10=1,D11=1),B8,IF(AND(B1+B3<B7,B2+B3>B8,D10=1),B7,IF(AND(B1+B3<B7,B2+B3<B8,D10=1,D11=1),B2+B3,IF(AND(B1+B3<B7,B2+B3<B8,B2+B3>B7,D10=1),B7,IF(AND(B2+B3<B7,D10=1),B2+B3,IF(B1+B3<B7,B1+B3,IF(AND(B2+B3<=B5,B1+B3>B7,D11=1,D12=1),B2+B3,IF(AND(B2+B3<=B5,B1+B3>B7,D10=1,D11=1),B8,IF(AND(OR(D12=1,D13=1),B2+B3<=B5,B1+B3>B7),B1+B3,IF(AND(B2+B3<=B5,B1+B3>B7),B7,IF(AND(B2+B3>=B5,D10=1,D11=1),B8,IF(AND(B2+B3>=B5,D11=1,D12=1),B8,IF(B2+B3>=B5,B7)))))))))))))))))

    // Check if B5 is blank (0 or undefined)
    if (!B5 && D11 && D12) {
      return B2 + B3
    } else if (!B5) {
      return B1 + B3
    } else if (B2 + B3 < B5 && D11 && D12) {
      return B2 + B3
    } else if (B1 + B3 < B7 && B2 + B3 > B8 && D10 && D11) {
      return B8
    } else if (B1 + B3 < B7 && B2 + B3 > B8 && D10) {
      return B7
    } else if (B1 + B3 < B7 && B2 + B3 < B8 && D10 && D11) {
      return B2 + B3
    } else if (B1 + B3 < B7 && B2 + B3 < B8 && B2 + B3 > B7 && D10) {
      return B7
    } else if (B2 + B3 < B7 && D10) {
      return B2 + B3
    } else if (B1 + B3 < B7) {
      return B1 + B3
    } else if (B2 + B3 <= B5 && B1 + B3 > B7 && D11 && D12) {
      return B2 + B3
    } else if (B2 + B3 <= B5 && B1 + B3 > B7 && D10 && D11) {
      return B8
    } else if ((D12 || D13) && B2 + B3 <= B5 && B1 + B3 > B7) {
      return B1 + B3
    } else if (B2 + B3 <= B5 && B1 + B3 > B7) {
      return B7
    } else if (B2 + B3 >= B5 && D10 && D11) {
      return B8
    } else if (B2 + B3 >= B5 && D11 && D12) {
      return B8
    } else if (B2 + B3 >= B5) {
      return B7
    }

    // Default fallback
    return 0
  })

  // Format function for read-only fields
  const formatToTwoDecimals = (value: number | string | null | undefined): string => {
    // Ensure value is a number before using toFixed
    const numValue = typeof value === 'number' ? value : Number(value || 0)
    return isNaN(numValue) ? '0.00' : numValue.toFixed(2)
  }

  // Format computed values for display in read-only fields
  const formattedNetRepairCosts = computed(() => formatToTwoDecimals(netRepairCosts.value))
  const formattedNetReplacementValue = computed(() =>
    formatToTwoDecimals(netReplacementValue.value)
  )
  const formattedNetGrossReplacementCosts = computed(() =>
    formatToTwoDecimals(netGrossReplacementCosts.value)
  )
  const formattedGrossGrossReplacementCosts = computed(() =>
    formatToTwoDecimals(grossGrossReplacementCosts.value)
  )
  const formattedTotalDamageCostsCalculated = computed(() =>
    formatToTwoDecimals(totalDamageCostsCalculated.value)
  )
</script>

<template>
  <div class="mt-2 flex h-full flex-col overflow-hidden">
    <div class="max-h-[67vh] flex-1 space-y-4 overflow-y-auto">
      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">
            {{ t('repairCosts') }}
          </h3>
          <InputField
            v-model="grossRepairCosts"
            asterisk
            suffix="€"
            :label="t('grossRepairCosts')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REPAIR_COSTS_WITH_VAT"
            data-cy="vehicleDamageInput-grossRepairCosts"
          />
          <InputField
            v-model="formattedNetRepairCosts"
            suffix="€"
            :label="t('netRepairCosts')"
            name="netRepairCosts"
            :disabled="true"
            data-cy="vehicleDamageResult-netRepairCosts"
          />
        </div>
      </div>

      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">{{ t('values') }}</h3>
          <InputField
            v-model="merantileValueLoss"
            asterisk
            suffix="€"
            :label="t('valueLoss')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_MERANTILE_VALUE_LOSS"
            data-cy="vehicleDamageInput-valueLoss"
          />
          <InputField
            v-model="grossReplacementValue"
            asterisk
            suffix="€"
            :label="t('grossReplacementValue')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REPLACEMENT_VALUE"
            data-cy="vehicleDamageInput-grossReplacementValue"
          />
          <InputField
            v-model="formattedNetReplacementValue"
            suffix="€"
            :label="t('netReplacementValue')"
            name="netReplacementValue"
            :disabled="true"
            data-cy="vehicleDamageResult-netReplacementValue"
          />
          <InputField
            v-model="grossResidualValue"
            asterisk
            suffix="€"
            :label="t('grossResidualValue')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_RESIDUAL_VALUE"
            data-cy="vehicleDamageInput-grossResidualValue"
          />
          <InputField
            v-model="formattedNetGrossReplacementCosts"
            suffix="€"
            :label="t('netGrossReplacementCosts')"
            name="netGrossReplacementCosts"
            :disabled="true"
            data-cy="vehicleDamageResult-netGrossReplacementCosts"
          />
          <InputField
            v-model="formattedGrossGrossReplacementCosts"
            suffix="€"
            :label="t('grossGrossReplacementCosts')"
            name="grossGrossReplacementCosts"
            :disabled="true"
            data-cy="vehicleDamageResult-grossGrossReplacementCosts"
          />
        </div>
      </div>

      <div class="space-y-4">
        <div class="grid grid-cols-2 items-center rounded-md bg-gray-50 p-3">
          <h3 class="col-span-2 pb-1 text-lg font-semibold text-gray-900">{{ t('options') }}</h3>
          <CheckboxField
            v-model="isReplacementPurchase"
            :label="t('isReplacementPurchase')"
            name="isReplacementPurchase"
            data-cy="vehicleDamageInput-isReplacementPurchase"
          />

          <CheckboxField
            v-model="isSixMonthExtension"
            :label="t('isSixMonthExtension')"
            name="isSixMonthExtension"
            data-cy="vehicleDamageInput-isSixMonthExtension"
          />
          <CheckboxField
            v-model="isRepair"
            :label="t('isRepair')"
            name="isRepair"
            data-cy="vehicleDamageInput-isRepair"
          />

          <CheckboxField
            v-model="isDifferentialTax"
            :label="t('isDifferentialTax')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_DIFFERENCIAL_TAX"
            data-cy="vehicleDamageInput-isDifferentialTax"
          />
          <CheckboxField
            v-model="isInvoiceWithVat"
            :label="t('isInvoiceWithVat')"
            name="isInvoiceWithVat"
            data-cy="vehicleDamageInput-isInvoiceWithVat"
          />
          <CheckboxField
            v-model="isRegularTax"
            :label="t('isRegularTax')"
            :name="DatamapperKey.LAW_TRAFFIC_LAW_REGULAR_TAX"
            data-cy="vehicleDamageInput-isRegularTax"
          />
        </div>
      </div>
    </div>
    <div class="mt-4">
      <h3 class="text-md pb-2 pl-1 font-normal text-gray-900">{{ t('total') }}</h3>
      <div class="flex items-center gap-4 px-1 pb-2">
        <div class="flex-grow">
          <InputField
            v-model="formattedTotalDamageCostsCalculated"
            suffix="€"
            name="totalDamageCosts"
            :disabled="true"
            data-cy="vehicleDamageResult-totalDamageCosts"
          />
        </div>
        <button
          :class="[
            'inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2 text-sm font-medium text-white',
            'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2',
            isSavingCase ? 'cursor-not-allowed bg-purple-400' : 'bg-purple-600 hover:bg-purple-700',
            'disabled:cursor-not-allowed disabled:opacity-50'
          ]"
          :disabled="isSavingCase"
          data-cy="vehicleDamageSaveBtn"
          @click="handleSaveAll"
        >
          <span v-if="isSavingCase">
            <Loader2 class="mr-1 inline h-4 w-4 animate-spin align-text-bottom" />
            {{ t('save') }}
          </span>
          <span v-else>
            {{ t('save') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>
